import { useEffect, useState } from 'react';
import type { DeviceModel } from '@techgitter/contracts';
import { lookupDeviceModels } from '../services/deviceModelsApi';

/**
 * Resolved lookups, kept for the life of the tab. The table is hand-curated
 * reference data that changes with a re-seed, not with anything a technician
 * does, so expanding the same card twice — or two cards for one model — costs
 * one request. Failures are not cached.
 */
const cache = new Map<string, DeviceModel[]>();

const cacheKey = (modelNumber: string, manufacturer: string | null | undefined) =>
  `${(manufacturer ?? '').trim().toUpperCase()}|${modelNumber.trim().toUpperCase()}`;

/** Test hook: forget every cached lookup. */
export function clearDeviceModelsCache(): void {
  cache.clear();
}

/**
 * The device model(s) a captured model number names. Pass a falsy number for
 * a device with none recorded, and nothing is fetched. `data` is `[]` both
 * before the answer and when the number is unknown — callers fall back to
 * showing the raw number, which they already do.
 */
export function useDeviceModels(
  modelNumber: string | null | undefined,
  manufacturer?: string | null,
): { data: DeviceModel[]; isLoading: boolean; error: string | null } {
  const number = modelNumber?.trim() || null;
  const key = number ? cacheKey(number, manufacturer) : null;
  const [data, setData] = useState<DeviceModel[]>(() => (key ? cache.get(key) ?? [] : []));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    if (!number || !key) {
      setData([]);
      setIsLoading(false);
      return;
    }
    const cached = cache.get(key);
    if (cached) {
      setData(cached);
      setIsLoading(false);
      return;
    }
    setData([]);
    const controller = new AbortController();
    setIsLoading(true);
    lookupDeviceModels(number, manufacturer, controller.signal)
      .then(items => {
        cache.set(key, items);
        setData(items);
      })
      .catch(err => {
        if (err?.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to look up the model');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
    // `key` folds in both inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data, isLoading, error };
}
